import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { BriefResponseDto } from '@/app/common/general-types/briefs.dto';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { BbSwitch } from '../../ui/bb-switch/bb-switch';
import { SOCIAL_CHANNELS, SocialChannelOption, VIDEO_DURATION_OPTIONS, VIDEO_FORMAT_OPTIONS, VideoDurationOption, VideoFormatOption } from '../../brief-builder.constants';
import {
  addChannel,
  addUgc,
  createDefaultPieceEditor,
  editorFromSavedPieces,
  type Piece,
  type PieceDuration,
  type PieceEditorState,
  type PieceFormat,
  piecesFromEditor,
  removeChannel,
  removePiece as removePieceAt,
  removeUgc,
  SOCIAL_PLACEMENT_CHANNELS,
  type SocialCopy,
  type SocialPlacementChannel,
  setPieceCount,
  setPieceDuration,
  setPieceFormat,
  setPieceLabel,
  setPlacementCopy,
  setShareCopy,
  setSharedCopy,
  setSharePlacements,
} from '@/app/common/brief-pieces/piece-list';
import { audienceFeesAt, estimateRankMultiplier, quotePieces, ratesFromPricingModel } from '@/app/common/brief-pieces/piece-price';

/**
 * Content section for the wizard and the AI review.
 * The brand sets 1 to 4 pieces and their placements. The saved brief stores those
 * placements, not the sharing toggles.
 */
@Component({
  selector: 'bb-content-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbSwitch, NgTemplateOutlet],
  templateUrl: './content-section.html',
  styleUrls: ['./content-section.scss'],
})
export class ContentSection {
  readonly service = inject(CreateBriefService);
  /**
   * Show the per-creator price estimate. Off on the Briefing tab, where the brief's real price
   * is on the Pay card and this estimate (from the builder's pricing state) doesn't apply.
   */
  readonly showPrice = input(true);
  readonly editor = signal<PieceEditorState>(this.restoreEditor());
  readonly channels: SocialChannelOption[] = SOCIAL_CHANNELS;
  /** Instagram, TikTok, YouTube, then Snapchat. Feed is not offered. */
  readonly placementGroups: { platform: string; channelIds: number[] }[] = [
    { platform: 'Instagram', channelIds: [1, 2] },
    { platform: 'TikTok', channelIds: [0] },
    { platform: 'YouTube', channelIds: [3] },
    { platform: 'Snapchat', channelIds: [4, 5] },
  ];
  readonly videoFormats: VideoFormatOption[] = VIDEO_FORMAT_OPTIONS;
  readonly videoDurations: VideoDurationOption[] = VIDEO_DURATION_OPTIONS;
  readonly pieceCount = computed(() => this.editor().pieces.length);
  private readonly copyOpen = signal<Record<string, boolean>>({});
  readonly postCount = computed(() =>
    this.editor().pieces.reduce((total, piece) => total + piece.channels.length, 0),
  );
  readonly currencySymbol = computed(() => this.service.pricingModelCurrencySymbol());
  /**
   * What the license adds per video. With it on, a UGC video is charged the advertising
   * rate instead of the UGC rate (see piece-price.ts), so the add-on is the difference.
   */
  readonly advertisingLicenseCostLabel = computed(() => {
    const model = this.service.selectedPricingModel();
    if (!model) {
      return null;
    }
    const amount = Math.max(0, (model.advertisingPrice ?? 0) - (model.ugcPrice ?? 0)) / 100;
    return (
      this.currencySymbol() +
      amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    );
  });
  readonly advertisingLicenseLocked = computed(() =>
    this.editor().pieces.some((piece) => piece.channels.length > 0),
  );
  readonly quoteLabel = computed(() => {
    const pieces = this.piecesToSave();
    if (!pieces?.length) {
      return null;
    }
    const model = this.service.selectedPricingModel();
    const rates = ratesFromPricingModel(model);
    const rankMultiplier = estimateRankMultiplier(model?.ranks);
    const posted = pieces.some((piece) => piece.placements.some((placement) => placement.kind === 'social'));
    const bonuses = posted && model?.influencerLevels?.length
      ? model.influencerLevels.map((level) => level.bonusValue / 100)
      : [0];
    const nets = bonuses.map((bonus) => quotePieces({
      pieces,
      rates,
      rankMultiplier,
      audienceFees: audienceFeesAt(pieces, bonus),
      adLicenseSelected: this.advertisingLicenseLocked() || this.service.selectedAdvertisingOnly(),
    }).net);
    return this.moneyRange(Math.min(...nets), Math.max(...nets));
  });

  constructor() {
    const requiresPieceList = this.service.requiresPieceList;
    if (typeof requiresPieceList === 'function' && requiresPieceList() && !this.piecesToSave()) {
      this.editor.set(
        addChannel(
          {
            ...this.editor(),
            sharedCopy: {
              caption: this.service.suggestedCaption(),
              hashtags: this.service.suggestedHastags(),
              callToActionLink: this.service.callToActionLink(),
            },
          },
          0,
          'tiktok_post',
        ),
      );
    }
    this.publishPieces();
  }

  setPieceCount(count: number): void {
    this.editor.update((state) => setPieceCount(state, count));
    this.publishPieces();
  }

  addPiece(): void {
    this.setPieceCount(this.pieceCount() + 1);
  }

  addUgc(index: number): void {
    this.editor.update((state) => addUgc(state, index));
    this.publishPieces();
  }

  addChannel(index: number, channel: SocialPlacementChannel): void {
    this.editor.update((state) => addChannel(state, index, channel));
    this.publishPieces();
  }

  setSharePlacements(enabled: boolean): void {
    this.editor.update((state) => setSharePlacements(state, enabled));
    this.publishPieces();
  }

  setShareCopy(enabled: boolean): void {
    if (!enabled) {
      this.editor.update((state) => setShareCopy(state, false));
      this.publishPieces();
      return;
    }
    const source = this.firstFilledCopy();
    const openShared = this.anyDisclosureOpen();
    this.editor.update((state) => setSharedCopy(setShareCopy(state, true), source));
    if (openShared) {
      this.copyOpen.update((state) => ({ ...state, shared: true }));
    }
    this.publishPieces();
  }

  setSharedCopy(copy: SocialCopy): void {
    this.editor.update((state) => setSharedCopy(state, copy));
    this.publishPieces();
  }

  setPlacementCopy(index: number, channel: SocialPlacementChannel, copy: SocialCopy): void {
    this.editor.update((state) => setPlacementCopy(state, index, channel, copy));
    this.publishPieces();
  }

  setPieceLabel(index: number, label: string): void {
    this.editor.update((state) => setPieceLabel(state, index, label));
    this.publishPieces();
  }

  setPieceFormat(index: number, format: PieceFormat): void {
    this.editor.update((state) => setPieceFormat(state, index, format));
    this.publishPieces();
  }

  setPieceDuration(index: number, durationSeconds: PieceDuration): void {
    this.editor.update((state) => setPieceDuration(state, index, durationSeconds));
    this.publishPieces();
  }

  piecesToSave(): Piece[] | null {
    return piecesFromEditor(this.editor());
  }

  private restoreEditor(): PieceEditorState {
    return this.independentPieces(this.savedOrDefault());
  }

  private savedOrDefault(): PieceEditorState {
    const savedEditor = this.service.pieceEditor();
    if (savedEditor) {
      return savedEditor;
    }
    const savedPieces = this.service.pieces();
    if (savedPieces?.length) {
      this.service.reopenDraft({
        title: this.service.basicInfoModel().briefTitle,
        pieces: savedPieces,
      } as BriefResponseDto);
      const restored = this.service.pieceEditor();
      if (restored) {
        return restored;
      }
      return editorFromSavedPieces(savedPieces);
    }
    return createDefaultPieceEditor({
      caption: this.service.suggestedCaption(),
      hashtags: this.service.suggestedHastags(),
      callToActionLink: this.service.callToActionLink(),
    });
  }

  /** Each piece has its own placements and copy. Shared captions are copied onto each placement first. */
  private independentPieces(state: PieceEditorState): PieceEditorState {
    return setSharePlacements(setShareCopy(state, false), false);
  }

  private publishPieces(): void {
    this.service.pieceEditor.set(this.editor());
    const pieces = this.piecesToSave();
    this.service.pieces.set(pieces);
    this.service.videosPerCreator.set(Math.max(1, pieces?.length ?? 0));
    this.includeLicenseWhenPosted();
  }

  private includeLicenseWhenPosted(): void {
    if (this.advertisingLicenseLocked()) {
      this.service.selectedAdvertisingOnly.set(true);
    }
  }

  private moneyRange(min: number, max: number): string {
    const money = (value: number) =>
      this.currencySymbol() + value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return min === max ? money(min) : `${money(min)} – ${money(max)}`;
  }

  hasUgc(index: number): boolean {
    const piece = this.placementSource(index);
    return piece?.ugc ?? false;
  }

  hasChannel(index: number, channelId: number): boolean {
    const channel = SOCIAL_PLACEMENT_CHANNELS[channelId];
    const piece = this.placementSource(index);
    return !!channel && !!piece?.channels.includes(channel);
  }

  toggleUgc(index: number): void {
    const target = this.placementIndex(index);
    this.editor.update((state) => {
      const piece = state.pieces[target];
      if (!piece || piece.ugc) {
        return state;
      }
      let next = state;
      for (const channel of piece.channels) {
        next = removeChannel(next, target, channel);
      }
      return addUgc(next, target);
    });
    this.publishPieces();
  }

  toggleChannel(index: number, channelId: number): void {
    const channel = SOCIAL_PLACEMENT_CHANNELS[channelId];
    if (!channel) {
      return;
    }
    const target = this.placementIndex(index);
    this.editor.update((state) => {
      const selected = !!state.pieces[target]?.channels.includes(channel);
      if (selected) {
        return removeChannel(state, target, channel);
      }
      return addChannel(removeUgc(state, target), target, channel);
    });
    this.publishPieces();
  }

  removePiece(index: number): void {
    this.editor.update((state) => this.reletterDefaults(removePieceAt(state, index)));
    this.publishPieces();
  }

  channelsOn(index: number): SocialPlacementChannel[] {
    const piece = this.editor().pieces[index];
    if (!piece) {
      return [];
    }
    return SOCIAL_PLACEMENT_CHANNELS.filter((channel) => piece.channels.includes(channel));
  }

  showsSharedCopy(): boolean {
    return this.editor().shareCopy && this.editor().pieces.some((piece) => piece.channels.length > 0);
  }

  copyFor(index: number, channel: SocialPlacementChannel): SocialCopy {
    const state = this.editor();
    if (state.shareCopy) {
      return state.sharedCopy;
    }
    return state.placementCopy[`${index}:${channel}`] ?? state.sharedCopy;
  }

  onPieceLabel(index: number, event: Event): void {
    const value = event.target instanceof HTMLInputElement ? event.target.value : '';
    this.setPieceLabel(index, value);
  }

  onSharedCaption(event: Event): void {
    this.setSharedCopy({ ...this.editor().sharedCopy, caption: this.inputValue(event) });
  }

  onSharedHashtags(event: Event): void {
    this.setSharedCopy({ ...this.editor().sharedCopy, hashtags: this.inputValue(event) });
  }

  onSharedLink(event: Event): void {
    this.setSharedCopy({ ...this.editor().sharedCopy, callToActionLink: this.inputValue(event) });
  }

  onPlacementCaption(index: number, channel: SocialPlacementChannel, event: Event): void {
    this.setPlacementCopy(index, channel, { ...this.copyFor(index, channel), caption: this.inputValue(event) });
  }

  onPlacementHashtags(index: number, channel: SocialPlacementChannel, event: Event): void {
    this.setPlacementCopy(index, channel, { ...this.copyFor(index, channel), hashtags: this.inputValue(event) });
  }

  onPlacementLink(index: number, channel: SocialPlacementChannel, event: Event): void {
    this.setPlacementCopy(index, channel, { ...this.copyFor(index, channel), callToActionLink: this.inputValue(event) });
  }

  formatFor(formatId: number): PieceFormat {
    if (formatId === 1) {
      return 'horizontal';
    }
    if (formatId === 2) {
      return 'square';
    }
    return 'vertical';
  }

  durationLabel(durationId: number): string {
    if (durationId === 15) {
      return '15s';
    }
    if (durationId === 60) {
      return '60s';
    }
    return '30s';
  }

  durationFor(durationId: number): PieceDuration {
    if (durationId === 15 || durationId === 60) {
      return durationId;
    }
    return 30;
  }

  pieceWord(): string {
    return this.pieceCount() === 1 ? 'piece' : 'pieces';
  }

  postWord(): string {
    return this.postCount() === 1 ? 'post' : 'posts';
  }

  /** One shared controls row only when several pieces use the same placements. */
  showSharedPlacements(): boolean {
    return this.pieceCount() > 1 && this.editor().sharePlacements;
  }

  /** Same copy for several pieces sits in one block under the pieces, not inside each row. */
  showSharedCopyCard(): boolean {
    return this.editor().shareCopy && this.pieceCount() > 1
      && this.editor().pieces.some((piece) => piece.channels.length > 0);
  }

  pieceIsUgc(index: number): boolean {
    const piece = this.editor().pieces[index];
    return !!piece && piece.ugc && piece.channels.length === 0;
  }

  channelsIn(channelIds: readonly number[]): SocialChannelOption[] {
    return channelIds.flatMap((channelId) => {
      const channel = this.channels.find((option) => option.channelId === channelId);
      return channel ? [channel] : [];
    });
  }

  groupIcon(channelIds: readonly number[]): string {
    return this.channelsIn(channelIds)[0]?.channelIcon ?? '';
  }

  placementButtonLabel(channel: SocialChannelOption): string {
    if (channel.channelId === 3) {
      return 'Short';
    }
    return this.channelDetail(channel.channelName) || channel.channelName;
  }

  placementAria(platform: string, channel: SocialChannelOption): string {
    return `${platform} ${this.placementButtonLabel(channel)}`;
  }

  pieceNeedsPlacement(index: number): boolean {
    const piece = this.placementSource(index);
    return !!piece && !piece.ugc && piece.channels.length === 0;
  }

  showsCopy(index: number, shared: boolean): boolean {
    if (shared) {
      return this.editor().pieces.some((piece) => piece.channels.length > 0);
    }
    return (this.editor().pieces[index]?.channels.length ?? 0) > 0;
  }

  showsChannelHeading(index: number): boolean {
    return !this.editor().shareCopy && this.channelsOn(index).length > 1;
  }

  copyDisclosureLabel(index: number, shared: boolean): string {
    if (shared) {
      return 'Post copy · all pieces';
    }
    if (this.showSharedPlacements()) {
      const name = this.editor().pieces[index]?.label.trim();
      return name ? `Post copy · ${name}` : 'Post copy';
    }
    return 'Post copy';
  }

  copyPreviewFor(index: number, shared: boolean): string {
    const parts = this.copyParts(index, shared);
    return parts.length ? parts.join(' · ') : 'Caption, CTA, hashtags';
  }

  copyExpanded(index: number, shared: boolean): boolean {
    const stored = this.copyOpen()[this.copyStateKey(index, shared)];
    if (stored !== undefined) {
      return stored;
    }
    return this.pieceCount() === 1 && this.copyParts(index, shared).length > 0;
  }

  toggleCopy(index: number, shared: boolean): void {
    const key = this.copyStateKey(index, shared);
    const next = !this.copyExpanded(index, shared);
    this.copyOpen.update((state) => ({ ...state, [key]: next }));
  }

  pieceLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  channelDetail(name: string): string {
    const space = name.indexOf(' ');
    return space < 0 ? '' : name.slice(space + 1);
  }

  ugcPillClass(selected: boolean): string {
    const base = 'inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-colors';
    return selected
      ? `${base} border-solid border-brand-studio bg-brand-studio text-white`
      : `${base} border-dashed border-foreground/30 text-muted-foreground hover:border-brand-studio hover:text-brand-studio`;
  }

  segmentButtonClass(selected: boolean, first: boolean): string {
    const border = first ? '' : 'border-l border-border';
    const base = `${border} px-2.5 text-xs font-semibold transition-colors`;
    return selected
      ? `${base} bg-brand-studio text-white`
      : `${base} bg-card text-muted-foreground hover:bg-brand-studio/10 hover:text-brand-studio`;
  }

  pieceRowClass(first: boolean): string {
    const base = 'flex flex-col gap-2 border-t border-border py-2.5';
    return first ? `${base} border-t-0 pt-0` : base;
  }

  chevronClass(open: boolean): string {
    return open
      ? 'size-4 shrink-0 transition-transform rotate-90'
      : 'size-4 shrink-0 transition-transform';
  }

  copyInputClass(): string {
    return 'w-full min-w-0 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-normal text-foreground outline-none placeholder:text-muted-foreground focus:border-brand-studio focus:ring-2 focus:ring-brand-studio/25';
  }

  copyFieldClass(): string {
    return 'flex min-w-0 flex-col gap-1 text-xs font-bold text-muted-foreground';
  }

  channelLabel(channel: SocialPlacementChannel): string {
    const channelId = SOCIAL_PLACEMENT_CHANNELS.indexOf(channel);
    return this.channels.find((option) => option.channelId === channelId)?.channelName ?? channel;
  }

  private firstFilledCopy(): SocialCopy {
    const state = this.editor();
    if (!state.shareCopy) {
      for (const [index, piece] of state.pieces.entries()) {
        for (const channel of piece.channels) {
          const copy = state.placementCopy[`${index}:${channel}`];
          if (copy && this.filledCopy(copy).length > 0) {
            return { ...copy };
          }
        }
      }
    }
    return { ...state.sharedCopy };
  }

  private anyDisclosureOpen(): boolean {
    return Object.values(this.copyOpen()).some((open) => open);
  }

  private reletterDefaults(state: PieceEditorState): PieceEditorState {
    return {
      ...state,
      pieces: state.pieces.map((piece, index) =>
        /^Video \d+$/.test(piece.label.trim()) ? { ...piece, label: `Video ${index + 1}` } : piece,
      ),
    };
  }

  private copyStateKey(index: number, shared: boolean): string {
    return shared ? 'shared' : String(index);
  }

  private copyParts(index: number, shared: boolean): string[] {
    if (shared || this.editor().shareCopy) {
      return this.filledCopy(this.editor().sharedCopy);
    }
    const seen = new Set<string>();
    const parts: string[] = [];
    for (const channel of this.channelsOn(index)) {
      for (const part of this.filledCopy(this.copyFor(index, channel))) {
        if (!seen.has(part)) {
          seen.add(part);
          parts.push(part);
        }
      }
    }
    return parts;
  }

  private filledCopy(copy: SocialCopy): string[] {
    return [copy.caption, copy.callToActionLink, copy.hashtags]
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }

  private placementIndex(index: number): number {
    return this.editor().sharePlacements ? 0 : index;
  }

  private placementSource(index: number) {
    return this.editor().pieces[this.placementIndex(index)];
  }

  private inputValue(event: Event): string {
    return event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement
      ? event.target.value
      : '';
  }

  onAdvertisingLicenseChange(enabled: boolean): void {
    if (this.advertisingLicenseLocked()) {
      this.service.selectedAdvertisingOnly.set(true);
      return;
    }
    this.service.selectedAdvertisingOnly.set(enabled);
  }
}

